import Axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ConfigBase } from '../types/config';
import { Utils } from '../utils/';
import * as Auth from './auth/';
import { AuthState } from './auth/Types';

type AbstractRootState = {
    authState: AuthState,
} & Record<any, any>;

type myThunkAction<R> = ThunkAction<R, AbstractRootState, undefined, Action>;
type myAxiosThunkAction = myThunkAction<Promise<AxiosResponse>>;
type myThunkDispatch = ThunkDispatch<AbstractRootState, undefined, Action>;

export type ServiceActions = {
    post: (url: string, data: any, contentType: string | null, accept: string | null) => Promise<AxiosResponse>,
    get: (url: string, params: any, contentType: string | null, accept: string | null) => Promise<AxiosResponse>,
    put: (url: string, data: any, contentType: string | null, accept: string | null) => Promise<AxiosResponse>,
    del: (url: string, params: any, contentType: string | null, accept: string | null) => Promise<AxiosResponse>,
    logFrontendError: (error: any) => Promise<AxiosResponse>,
};

export const createServiceActions = (config: ConfigBase) => {
    const sendRequest = async (method: Method, url: string, params: any, data: any, headers: any, accept: string | null) => {
        const axiosRequest: AxiosRequestConfig = {
            method,
            url,
            params,
            data,
            headers,
            responseType: (accept === 'application/octet-stream' || accept === 'application/pdf') ? 'blob' : 'json',
        };
        return await Axios(axiosRequest);
    };

    const call = (verb: Method, url: string, paramsOrData: any, contentType: string | null, accept: string | null): myThunkAction<Promise<any>> => {
        return (dispatch, getState) => {
            const promiseCall = new Promise((resolve, reject) => {
                const authServiceActions = Auth.Service.bindServiceActionCreators(config, dispatch);

                // Apr 2020: AT stored on a cookie
                // let accessToken = sessionStorage.getItem(config.AccessTokenAlias);
                let accessToken = authServiceActions.getActualAccessToken();

                const headers: any = Object.assign({},
                    accept ? { Accept: accept } : null,
                    contentType ? { 'Content-Type': contentType } : null,
                );

                if (accessToken) {
                    headers.AT = accessToken;
                }

                let getParams: any = null;
                let postData: any = null;
                if (verb === 'get') {
                    getParams = paramsOrData;
                } else {
                    postData = paramsOrData;
                }

                sendRequest(
                    verb,
                    config.ApiServerHostName + config.ApiURL + url,
                    getParams,
                    postData,
                    headers,
                    accept,
                ).then(
                    Utils.checkHttpStatus,
                ).then(
                    (response) => {
                        resolve(response);
                    },
                ).catch((error) => {
                    if (error.response && error.response.status === 401 && error.response.data !== null && (
                        error.response.data.message === 'Authorization has been denied for this request.' ||
                        error.response.data.Message === 'Authorization has been denied for this request.' ||
                        error.response.data instanceof Blob /* this happens when we are fetching binaries */
                    )) {
                        // the next piece of code stinks
                        // basically we're blocking any calls to get a new refresh_token while another apicaller is in the process of also obtaining a refresh_token
                        // if more that one request to obtain a refresh_token is running at the same time, all hell will break loose. Example (in a timeline fashion)
                        //      refresh_token (RT) stored in localStorage has value X - this is in sync with the Auth server
                        //      access_token expires.
                        //      apicaller1 (ex: getDealers) and apicaller2 (ex: getEvents) are triggered at the same time. they will both receive 401 Unauthorized due to the expired access_token
                        //      apicaller1 uses RT X to request (getnewrefreshtoken1) a new pair of tokens (both access and refresh)
                        //      apicaller2 uses RT X to request (getnewrefreshtoken2) a new pair of tokens (both access and refresh)
                        // at this point the order of events is non-deterministic, with all except the happy path (which is rare) resulting in a variety of problems / weird behaviour. An example of such:
                        //      getnewrefreshtoken1 is processed and a new pair of tokens is returned. particularly RT X is replaced with RT Y - this is in sync with the Auth server
                        //      apicaller1 resumes the original call (getDealers) with the shiny new access_token
                        //      at this point the Auth server starts processing getnewrefreshtoken2. There is a mismatch between expected RT Y and received RT X
                        //      Auth server returns "invalid_grant". As a result our react app will immediately clear all local store and redirect us to the login page with a message "session expired, please re-login"
                        //      meanwhile apicaller1 finishes the call (getDealers) ... which will hydrate its redux store... after we cleared it
                        // things get even weirder if getnewrefreshtoken1 is hung up (probably during a database call) and getnewrefreshtoken2 returns immediately with an error, triggering the local store clear and redirect to login ...
                        // ... and then afterwards we receive getnewrefreshtoken1's response which will set the tokens in the localStorage, leaving a mess of inconsistency
                        //
                        // hope this wall of text helps justify the following semi-crappy code :P

                        // The first guy begins the process of obtaining a new refresh_token. That will set isObtainingNewRefreshToken from false to true
                        if (!getState().authState.isObtainingNewAccessToken) {
                            authServiceActions.useRefreshToken()
                                .then(() => {
                                    // after obtaining the new tokens he'll retry his original call
                                    // Apr 2020: AT stored on a cookie
                                    accessToken = authServiceActions.getActualAccessToken();
                                    sendRequest(
                                        verb,
                                        config.ApiServerHostName + config.ApiURL + url,
                                        getParams,
                                        postData,
                                        {...headers, AT: accessToken},
                                        accept,
                                    ).then(
                                        Utils.checkHttpStatus,
                                    ).then((response) => {
                                        resolve(response);
                                    }).catch((innerError) => {
                                        if (innerError.response.status === 401 && innerError.response.data !== null && (innerError.response.data.message === 'Authorization has been denied for this request.' || innerError.response.data.Message === 'Authorization has been denied for this request.')) {
                                            authServiceActions.logoutDueToNewAccessTokenFailure({
                                                response: null,
                                                errorCode: config.Constants.ErrorCodes.SessionExpired,
                                                errorMessage: null,
                                                exceptionType: null,
                                                exceptionData: null,
                                            });
                                        }
                                        reject(innerError);
                                    });
                                }).catch((innerError: Error) => {
                                    authServiceActions.logoutDueToNewAccessTokenFailure({
                                        response: null,
                                        errorCode: config.Constants.ErrorCodes.SessionExpired,
                                        errorMessage: null,
                                        exceptionType: null,
                                        exceptionData: null,
                                    });
                                    reject(innerError);
                                });
                        } else {
                            // So the second, third, and etc guys will enter here. They will wait until the boolean is false. After that they will trust the first guy obtained new tokens, therefore, they'll retry their original call
                            /* tslint:disable */
                            // shamelessly disabling lint for hasty pasted code
                            const waitUntil = function(condition: any, delay: any, func: any) {
                                let h: any;
                                if (!func) {
                                    func = delay;
                                    delay = 500;
                                }
                                const g = function() {
                                    if (condition()) {
                                        func();
                                        return clearInterval(h);
                                    }
                                };
                                return h = setInterval(g, delay);
                            };
                            /* tslint:enable */
                            waitUntil(() => !getState().authState.isObtainingNewAccessToken, () => {
                                // Apr 2020: AT stored on a cookie
                                // accessToken = sessionStorage.getItem(config.AccessTokenAlias);
                                accessToken = authServiceActions.getActualAccessToken();
                                sendRequest(
                                    verb,
                                    config.ApiServerHostName + config.ApiURL + url,
                                    getParams,
                                    postData,
                                    {...headers, AT: accessToken},
                                    accept,
                                ).then(
                                    Utils.checkHttpStatus,
                                ).then((response) => {
                                    resolve(response);
                                }).catch((innerError) => {
                                    authServiceActions.logoutDueToNewAccessTokenFailure({
                                        response: null,
                                        errorCode: config.Constants.ErrorCodes.SessionExpired,
                                        errorMessage: null,
                                        exceptionType: null,
                                        exceptionData: null,
                                    });
                                    reject(innerError);
                                });
                            }, null);
                        }
                    } else {
                        reject(error);
                    }
                });
            });
            return promiseCall;
        };
    };

    const get = (url: string, params: any, contentType: string | null = null, accept: string | null = 'application/json'): myAxiosThunkAction  => {
        return (dispatch) => {
            return dispatch(call('get', url, params, contentType, accept));
        };
    };

    const post = (url: string, data: any, contentType: string | null = 'application/json', accept: string | null = 'application/json'): myAxiosThunkAction => {
        return (dispatch) => {
            return dispatch(call('post', url, data, contentType, accept));
        };
    };

    const put = (url: string, data: any, contentType: string | null = 'application/json', accept: string | null = 'application/json'): myAxiosThunkAction => {
        return (dispatch) => {
            return dispatch(call('put', url, data, contentType, accept));
        };
    };

    const del = (url: string, params: any, contentType: string | null = 'application/json', accept: string | null = 'application/json'): myAxiosThunkAction => {
        return (dispatch) => {
            return dispatch(call('delete', url, params, contentType, accept));
        };
    };

    const logFrontendError = (error: any, hideData: boolean = false): myAxiosThunkAction => {
        if (hideData && 'config' in error && 'data' in error.config) {
            error.config.data = '#####Sensitive Data Removed#####';
        }
        return post('/api/prime/common/LogFrontEndError', error);
    };

    return {
        post,
        get,
        put,
        del,
        logFrontendError,
    };
};

export default (config: ConfigBase) => {
    return createServiceActions(config);
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: myThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        post: (url: string, data: any, contentType: string | null = 'application/json', accept: string | null = 'application/json') => dispatch(services.post(url, data, contentType, accept)),
        get: (url: string, params: any, contentType: string | null = null, accept: string | null = 'application/json') => dispatch(services.post(url, params, contentType, accept)),
        put: (url: string, data: any, contentType: string | null = 'application/json', accept: string | null = 'application/json') => dispatch(services.put(url, data, contentType, accept)),
        del: (url: string, params: any, contentType: string | null = 'application/json', accept: string | null = 'application/json') => dispatch(services.del(url, params, contentType, accept)),
        logFrontendError: (error: any) => dispatch(services.logFrontendError(error)),
    };
};
